import { flow, Instance, types } from 'mobx-state-tree'
import { paginationSettings } from '../../data/static/data'
import { RootStore } from '../../data/store'
import { cancelPaymentLinkRequest } from '../../network/cancelPaymentLink'
import { checkPaymentLinkStatusRequest } from '../../network/checkPaymentLinkStatus'
import { createPaymentLinkRequest } from '../../network/createPaymentLink'
import { getPaymentLinks } from '../../network/getPaymentLinks'
import { resendPaymentLinkRequest } from '../../network/sendPaymentLink'
import { getSelectedDate } from '../../util/util'
import { paymentLinkDetailsParser, paymentLinksTableParser } from '../parser/view/TableParser'
import { customError } from '../static/strings'
import { parsePaymentLinkError } from './../parser/view/ErrorParser'
import { Pagination } from './Transaction'

const LinkTransactions = types.model('LinkTransactions', {
  id: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  extTranRefNo: types.optional(types.string, ''),
  transactionRefNo: types.optional(types.string, ''),
})

const PaymentLinkDetailData = types.model('PaymentLinkDetailData', {
  url: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  amount: types.optional(types.string, ''),
  merchantLinkId: types.optional(types.string, ''),
  linkExpiryAt: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
  pushSmsTo: types.optional(types.string, ''),
  pushEmailTo: types.optional(types.string, ''),
  paidAt: types.optional(types.string, ''),
})

const Link = types.model('Link', {
  id: types.optional(types.string, ''),
  jioLinkId: types.optional(types.string, ''),
  url: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  amount: types.optional(types.string, ''),
  merchantLinkId: types.optional(types.string, ''),
  linkExpiryAt: types.optional(types.string, ''),
  invoice: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
  pushSmsTo: types.optional(types.string, ''),
  pushEmailTo: types.optional(types.string, ''),
  merchantReturnUrl: types.optional(types.string, ''),
  merchantCallBackUrl: types.optional(types.string, ''),
  customerName: types.optional(types.string, ''),
  updatedAt: types.optional(types.string, ''),
  cancelAt: types.optional(types.string, ''),
  paidAt: types.optional(types.string, ''),
  transactions: types.optional(types.array(LinkTransactions), []),
})

const getStatusShortValue = (value: string): string => {
  const shortValues: any = {
    PAID: 'P',
    EXPIRED: 'E',
    CANCELLED: 'CN',
    ISSUED: 'I',
    ALL: 'A',
  }
  return shortValues[value]
}

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.string,
})

const Links = types
  .model('Links', {
    link: types.array(Link),
    pagination: Pagination,
    isNewLinkCreated: types.optional(types.boolean, false),
    paymentLinkDetailData: types.optional(PaymentLinkDetailData, {}),
    selectedDateInfo: DateRange,
  })
  .actions((self) => ({
    fetchPaymentLinks: flow(function* fetchPaymentLinks(
      merchantId: string,
      _isLoadMore = false,
      fromDate: string,
      toDate: string,
      page,
      size,
      linkStatus?: string,
      email?: string,
      mobile?: string,
      merchantLinkId?: string,
      link?: string,
      amount?: string,
      invoiceNumber?: string,
      jioLinkId?: string,
    ) {
      const statusFilter = getStatusShortValue(RootStore.getSelectedStatusFilter())
      const emailFilter = RootStore.getEmailEnteredFilter()
      const mobileFilter = RootStore.getContactNumberEnteredFilter()
      const payLinkIdFilter = RootStore.getPayLinkIdEnteredFilter()
      const merchantLinkIdFilter = RootStore.getMerchantIdEnteredFilter()
      const amountFilter = RootStore.getAmountEnteredFilter()
      const invoiceNumberFilter = RootStore.getInvoiceNumberEnteredFilter()
      const own = self
      const lStatus = statusFilter && statusFilter.length > 0 ? statusFilter?.trim() : linkStatus?.trim()
      const querryParam = {
        linkStatus: lStatus !== 'A' ? lStatus : undefined, // if status is All or A we need not send in query param
        email: emailFilter && emailFilter !== '' ? emailFilter?.trim() : email?.trim(),
        mobile: mobileFilter && mobileFilter !== '' ? mobileFilter?.trim() : mobile?.trim(),
        merchantLinkId: merchantLinkIdFilter && merchantLinkIdFilter !== '' ? merchantLinkIdFilter : merchantLinkId,
        fromDate,
        toDate,
        jioLinkId: payLinkIdFilter && payLinkIdFilter !== '' ? payLinkIdFilter?.trim() : jioLinkId?.trim(),
        amount: amountFilter && amountFilter !== '' ? amountFilter : amount,
        invoiceNumber: invoiceNumberFilter && invoiceNumberFilter !== '' ? invoiceNumberFilter : invoiceNumber,
        link,
      }
      let response
      const req = {
        merchantId,
        page: page,
      }
      try {
        response = yield getPaymentLinks(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
          querryParam,
        )
        const parsedData = response?.links
        own.link = _isLoadMore
          ? JSON.parse(JSON.stringify([...own.link, ...parsedData]))
          : JSON.parse(JSON.stringify(parsedData))
        if (page === 0) {
          own.pagination = JSON.parse(JSON.stringify(response?.page))
        }
        if (response?.error && response?.error?.message) {
          RootStore?.response?.setMessage(response?.error?.message ?? customError.errorOccured)
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    createPaymentLink: flow(function* createPaymentLink(
      merchantId: string,
      merchantLinkId: string,
      amount: string,
      description?: string,
      pushSmsTo?: string,
      invoice?: string,
      linkExpiryAt?: string,
      pushEmailTo?: string,
      merchantReturnUrl?: string,
      merchantCallBackUrl?: string,
      properties?: any,
    ) {
      let response
      const req = {
        merchantId,
        merchantLinkId,
        amount,
        description,
        pushSmsTo,
        invoice,
        linkExpiryAt,
        merchantReturnUrl,
        merchantCallBackUrl,
        properties,
        pushEmailTo,
      }
      try {
        response = yield createPaymentLinkRequest(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const newLink = response?.link
        const own = self
        if (newLink) {
          own.link.push(newLink)
        }
        own.link = JSON.parse(JSON.stringify([...own.link]))
      } catch (e) {
        const additionalError = e?.data?.error?.additionalDetails
        RootStore?.response?.setMessage(parsePaymentLinkError(additionalError, e))
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    cancelPaymentLink: flow(function* cancelPaymentLink(merchantId: string, merchantLinkId: string, jioLinkId: string) {
      let response
      const req = {
        merchantId,
        merchantLinkId,
        jioLinkId,
      }
      try {
        response = yield cancelPaymentLinkRequest(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        console.log('cancelPaymentLink ', { response })
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    resendPaymentLink: flow(function* resendPaymentLink(merchantId: string, merchantLinkId: string, jioLinkId: string) {
      let response
      const req = {
        merchantId,
        merchantLinkId,
        jioLinkId,
      }
      try {
        response = yield resendPaymentLinkRequest(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        console.log('resendPaymentLink ', { response })
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    checkPaymentLinkStatus: flow(function* checkPaymentLinkStatus(
      merchantId: string,
      merchantLinkId: string,
      jioLinkId: string,
      page?: string,
    ) {
      let response
      const req = {
        merchantId,
        merchantLinkId,
        jioLinkId,
        page,
      }
      try {
        response = yield checkPaymentLinkStatusRequest(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const own = self
        const updatedLink = response?.link

        let newLinks = []

        own.link.forEach((item) => {
          if (item.jioLinkId === updatedLink?.jioLinkId) {
            newLinks.push(JSON.parse(JSON.stringify(updatedLink)))
          } else {
            newLinks.push(item)
          }
          return newLinks
        })

        own.link = JSON.parse(JSON.stringify(newLinks))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updateSelectedDateInfo(dateObj: any): void {
      const own = self
      own.selectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.selectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.selectedDateInfo.position.top = dateObj?.position?.top ?? 0
      own.selectedDateInfo.position.left = dateObj?.position?.left ?? 0
      own.selectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      own.selectedDateInfo.dateLabel = dateObj?.dateLabel ?? ''
    },
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettings))
    },
    updateNewLinkCreatedInStore(isLinkCreated: boolean) {
      const own = self
      own.isNewLinkCreated = isLinkCreated
    },
    setPaymentDetails(id: string) {
      const details = self.link.filter((item) => item.jioLinkId === id)
      if (details.length) {
        const data = paymentLinkDetailsParser(details[0], false)
        self.paymentLinkDetailData = data
      }
    },
    getPaymentDetails(id: string, { duplicate }: { duplicate: boolean }) {
      const details = self.link.filter((item) => item.jioLinkId === id)
      if (details.length) {
        if (duplicate) {
          const getData = paymentLinkDetailsParser(details[0], duplicate)
          self.paymentLinkDetailData = getData
        }
        //Removes the invoice number
        return paymentLinkDetailsParser(details[0], duplicate)
      }
      return {}
    },
  }))
  .views((self) => ({
    getPaymentLinksData(isSmallDevice: boolean): any[] {
      const links = paymentLinksTableParser(self.link)
      if (links.length === 0) {
        return []
      }
      if (isSmallDevice) {
        return links.slice(0)
      }
      return [...links.slice(0, 10)]
    },

    paymentLinkData(): any {
      return self.paymentLinkDetailData
    },
    getMerchantLinkIdFromJioLinkId(jioLinkId: string): string {
      const details = self.link.filter((item) => item.jioLinkId === jioLinkId)[0]
      return details?.merchantLinkId ?? ''
    },
    returnPaymentLinkFilterCount() {
      let count = 0
      const statusFilter = getStatusShortValue(RootStore.getSelectedStatusFilter())
      const emailFilter = RootStore.getEmailEnteredFilter()
      const mobileFilter = RootStore.getContactNumberEnteredFilter()
      const payLinkIdFilter = RootStore.getPayLinkIdEnteredFilter()
      const merchantLinkIdFilter = RootStore.getMerchantIdEnteredFilter()
      const amountFilter = RootStore.getAmountEnteredFilter()
      const invoiceNumberFilter = RootStore.getInvoiceNumberEnteredFilter()

      if (statusFilter?.length > 0 && statusFilter !== 'A') {
        count++
      }
      if (emailFilter !== '') {
        count++
      }
      if (mobileFilter !== '') {
        count++
      }
      if (payLinkIdFilter !== '') {
        count++
      }
      if (merchantLinkIdFilter !== '') {
        count++
      }
      if (amountFilter !== '') {
        count++
      }
      if (invoiceNumberFilter !== '') {
        count++
      }
      return { count: count, isApplied: count > 0 }
    },
  }))

const LinksInitialData = Links.create({
  link: [],
  pagination: paginationSettings,
  selectedDateInfo: {
    fromDate: getSelectedDate(0),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
    dateLabel: 'Today',
  },
})

export type paymentType = Instance<typeof Links>
export { Link, Links, LinksInitialData, LinkTransactions }
